import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { FiArrowLeft, FiArrowRight, FiCheck } from "react-icons/fi";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui/primitives";
import { useRegisterWrappedView, useWrapped, useWrappedList } from "@/hooks/useWrapped";
import { WrappedCard } from "../components/WrappedCard";
import { WrappedEmptyState } from "../components/WrappedEmptyState";
import { WrappedHistoryList } from "../components/WrappedHistoryList";
import type { WrappedListItem, WrappedReport } from "../types";

export const WrappedView = () => {
  const { t } = useTranslation("wrapped");
  const navigate = useNavigate();
  const { period } = useParams({ strict: false });
  const reportQuery = useWrapped(period);
  const historyQuery = useWrappedList();
  const registerView = useRegisterWrappedView(period ? "historical" : "latest");
  const [currentIndex, setCurrentIndex] = useState(0);
  const registeredReportIds = useRef(new Set<number>());
  const report = reportQuery.data as WrappedReport | null | undefined;
  const cards = report?.cards ?? [];
  const activeCard = cards[currentIndex];

  useEffect(() => {
    setCurrentIndex(0);
  }, [report?.id]);

  useEffect(() => {
    if (report?.status !== "READY" || registeredReportIds.current.has(report.id)) return;

    registeredReportIds.current.add(report.id);
    registerView.mutate(report.id);
  }, [registerView, report?.id, report?.status]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (report?.status !== "READY" || cards.length < 2) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setCurrentIndex((index) => Math.max(0, index - 1));
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setCurrentIndex((index) => Math.min(cards.length - 1, index + 1));
    }
  };

  const openPeriod = (targetPeriod: string) => {
    void navigate({ to: "/wrapped/$period", params: { period: targetPeriod } });
  };

  const reportState = useMemo(() => {
    if (reportQuery.isLoading) return "loading" as const;
    if (reportQuery.isError) return "error" as const;
    if (!report) return "not-found" as const;
    if (report.status !== "READY" || !activeCard) return "empty" as const;
    return "ready" as const;
  }, [activeCard, report, reportQuery.isError, reportQuery.isLoading]);

  return (
    <div className="app-page overflow-x-hidden">
      <div className="app-container mx-auto max-w-7xl">
        <header className="app-intro">
          <div>
            <h1 className="app-title">{t("wrapped.title")}</h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--app-text-secondary)] sm:text-base">{t("wrapped.subtitle")}</p>
          </div>
        </header>

        <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)] lg:items-start">
          <main className="min-w-0" tabIndex={-1} onKeyDown={handleKeyDown} aria-live="polite">
            {reportState === "loading" ? (
              <section className="app-panel-raised flex min-h-64 items-center justify-center p-6 text-center" aria-busy="true">
                <p className="text-sm text-[var(--app-text-secondary)]" role="status">{t("wrapped.loading")}</p>
              </section>
            ) : null}
            {reportState === "error" ? <WrappedEmptyState isError /> : null}
            {reportState === "not-found" ? <WrappedEmptyState isNotFound /> : null}
            {reportState === "empty" && report ? <WrappedEmptyState status={report.status} reason={report.ineligible_reason} /> : null}

            {reportState === "ready" && activeCard ? (
              <div className="min-w-0">
                <WrappedCard card={activeCard} />
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3" aria-label={t("wrapped.progress", { current: currentIndex + 1, total: cards.length })}>
                  <AppButton
                    type="button"
                    tone="ghost"
                    size="sm"
                    leadingIcon={<FiArrowLeft aria-hidden="true" />}
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
                  >
                    {t("wrapped.previous")}
                  </AppButton>
                  <div className="flex items-center gap-2" role="img" aria-label={t("wrapped.progress", { current: currentIndex + 1, total: cards.length })}>
                    {cards.map((card, index) => (
                      <span
                        key={`${card.type}-${index}`}
                        className={`h-2.5 w-2.5 rounded-full transition-colors ${index === currentIndex ? "bg-[var(--app-primary)]" : "bg-[var(--app-border-strong)]"}`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <AppButton
                    type="button"
                    tone={currentIndex === cards.length - 1 ? "primary" : "ghost"}
                    size="sm"
                    leadingIcon={currentIndex === cards.length - 1 ? <FiCheck aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}
                    onClick={() => {
                      if (currentIndex === cards.length - 1) {
                        void navigate({ to: "/dashboard" });
                        return;
                      }
                      setCurrentIndex((index) => Math.min(cards.length - 1, index + 1));
                    }}
                  >
                    {t(currentIndex === cards.length - 1 ? "wrapped.done" : "wrapped.next")}
                  </AppButton>
                </div>
              </div>
            ) : null}
          </main>

          <aside className="min-w-0">
            <WrappedHistoryList
              reports={(historyQuery.data ?? []) as WrappedListItem[]}
              isLoading={historyQuery.isLoading}
              isError={historyQuery.isError}
              onOpenPeriod={openPeriod}
            />
          </aside>
        </div>
      </div>
    </div>
  );
};
